// Per-user staging area for training corpora, backed by Supabase Storage.
// Files live at:
//
//     training-corpora/<user_id>/staged/<timestamp>-<safe_name>
//
// Per the bucket's RLS policy (see supabase/migrations/20260509181800),
// authenticated users can read/write/delete only their own folder. So
// every device the user signs in to sees the same staged corpus, while
// no other user can see it.
//
// When the user kicks off training, CorpusUploadService reads everything
// under <user_id>/staged/ , bundles it into a single normalized JSONL,
// and puts that bundle at <user_id>/<request_id>/corpus.jsonl. The
// staged files stay in place so the user can keep iterating.

import { supabase } from '@/integrations/supabase/client';

const BUCKET = 'training-corpora';

export interface StagedCorpusFile {
  path: string;          // full path inside the bucket: '<user_id>/staged/<filename>'
  name: string;          // display filename (no timestamp prefix)
  size: number;
  modified: string;      // ISO
  contentType: string;
}

function staged(userId: string) {
  return `${userId}/staged`;
}

function safeName(raw: string): string {
  return raw.replace(/[<>:"/\\|?* -]/g, '-').replace(/\s+/g, ' ').trim();
}

function stripTimestampPrefix(name: string): string {
  // We prefix uploads with a millisecond timestamp + dash to avoid name
  // collisions in the bucket. Strip it for display.
  return name.replace(/^\d{10,16}-/, '');
}

export const CorpusStorage = {
  async list(userId: string): Promise<StagedCorpusFile[]> {
    if (!userId) return [];
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .list(staged(userId), {
        limit: 200,
        sortBy: { column: 'updated_at', order: 'desc' },
      });
    if (error) throw new Error(`list corpus failed: ${error.message}`);
    return (data || [])
      .filter((entry) => entry.name && !entry.name.endsWith('/'))
      .map((entry) => ({
        path: `${staged(userId)}/${entry.name}`,
        name: stripTimestampPrefix(entry.name),
        size: Number((entry.metadata as { size?: number } | null)?.size || 0),
        modified: entry.updated_at || entry.created_at || new Date().toISOString(),
        contentType: String((entry.metadata as { mimetype?: string } | null)?.mimetype || 'application/octet-stream'),
      }));
  },

  async upload(userId: string, file: File): Promise<StagedCorpusFile> {
    const ts = Date.now();
    const name = `${ts}-${safeName(file.name) || `corpus-${ts}.txt`}`;
    const path = `${staged(userId)}/${name}`;
    const { error } = await supabase.storage
      .from(BUCKET)
      .upload(path, file, {
        cacheControl: '0',
        contentType: file.type || 'application/octet-stream',
        upsert: false,
      });
    if (error) throw new Error(`upload failed: ${error.message}`);
    return {
      path,
      name: stripTimestampPrefix(name),
      size: file.size,
      modified: new Date().toISOString(),
      contentType: file.type || 'application/octet-stream',
    };
  },

  async delete(path: string): Promise<void> {
    const { error } = await supabase.storage.from(BUCKET).remove([path]);
    if (error) throw new Error(`delete failed: ${error.message}`);
  },

  async clear(userId: string): Promise<number> {
    const files = await CorpusStorage.list(userId);
    if (!files.length) return 0;
    const { error } = await supabase.storage
      .from(BUCKET)
      .remove(files.map((f) => f.path));
    if (error) throw new Error(`clear failed: ${error.message}`);
    return files.length;
  },

  async readText(path: string): Promise<string> {
    const { data, error } = await supabase.storage.from(BUCKET).download(path);
    if (error) throw new Error(`download failed: ${error.message}`);
    return await data.text();
  },
};
